import mixpanel from 'mixpanel-browser'
import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
// import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { AnnouncementNotification } from 'src/components/AnnouncementNotification/AnnouncementNotification'
// import { ConsultantDetailContainer } from 'src/components/ConsultantDetailContainer/ConsultantDetailContainer'
// import { ConsultantEnhancedDetailContainer } from 'src/components/ConsultantEnhancedDetailContainer/ConsultantEnhancedDetailContainer'
import { EnhancedRequestContainer } from 'src/components/EditListing/EnhancedRequestContainer'
import { Footer } from 'src/components/Footer/Footer'
//import { Breadcrumb } from 'src/components/global/Breadcrumb'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
///import { SolutionEnhancedHeader } from 'src/components/SolutionsPage/SolutionEnhancedHeader/SolutionEnhancedHeader'
//import { SolutionHeader } from 'src/components/SolutionsPage/SolutionHeader/SolutionHeader'
// import { Testimonials } from 'src/components/Testimonials/Testimonials'
import * as enVariables from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import {
  IConsultancySolutionDataFragment,
  ISimilarSolutionItemFragment,
  ITestimonialItemFragment,
  useSimilarSolutionsLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { Meta } from 'src/layout/Meta'
import { Main } from 'src/templates/Main'
import { FilterOption, REVIEWS_TAB, SimpleSubTopicType } from 'src/types'
import { getAuthorsFilter, getDateFilter } from 'src/utils/ContentFilters/contentFilters'

import { SolutionBreadcrumb } from '../../components/SolutionBreadcrumb/SolutionBreadcrumb'
import { AppState } from '../../store/types'

type Props = {
  solution: IConsultancySolutionDataFragment
  testimonials: ITestimonialItemFragment[]
  showVendorAnalysis: boolean
  vendorUsers: string[]
  _testimonials: ITestimonialItemFragment[]
}

const Breadcrumb = dynamic(() => import('src/components/global/Breadcrumb').then((mod) => mod.Breadcrumb), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})
const SolutionEnhancedHeader = dynamic(
  () =>
    import('src/components/SolutionsPage/SolutionEnhancedHeader/SolutionEnhancedHeader').then(
      (mod) => mod.SolutionEnhancedHeader,
    ),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const SolutionHeader = dynamic(
  () => import('src/components/SolutionsPage/SolutionHeader/SolutionHeader').then((mod) => mod.SolutionHeader),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const ConsultantEnhancedDetailContainer = dynamic(
  () =>
    import('src/components/ConsultantEnhancedDetailContainer/ConsultantEnhancedDetailContainer').then(
      (mod) => mod.ConsultantEnhancedDetailContainer,
    ),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const ConsultantDetailContainer = dynamic(
  () =>
    import('src/components/ConsultantDetailContainer/ConsultantDetailContainer').then(
      (mod) => mod.ConsultantDetailContainer,
    ),
  {
    ssr: false,
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const Testimonials = dynamic(() => import('src/components/Testimonials/Testimonials').then((mod) => mod.Testimonials), {
  ssr: false,
  loading: () => <SkeletonLoading className="bg-navy" />,
})

const ConsultantPage: NextPage<Props> = ({
  solution,
  testimonials,
  showVendorAnalysis,
  vendorUsers,
  _testimonials,
}: Props) => {
  const originInfo = useSelector((state: AppState) => state.originInfo)
  const prevPath = originInfo.lastOrigin || ''
  const router = useRouter()
  const [topicInfo, setTopicInfo] = useState<SimpleSubTopicType>()
  const [subTopicInfo, setSubTopicInfo] = useState<SimpleSubTopicType>()

  const [tabIdx, setTabIdx] = useState(0)
  const onSelectTab = (index: number) => setTabIdx(index)
  const imageUrl = useDefaultLogo(solution.attributes?.logo?.data?.attributes?.url)
  const displayName = solution.attributes?.displayName || ''
  const shortDescription = solution.attributes?.shortDescription || ''
  const slug = solution.attributes?.slug || ''
  const isEnhanced = solution.attributes?.isEnhanced
  const isPremium = solution.attributes?.isPremium
  const { announcement, optionalLink, publishedAt } = solution.attributes?.announcement?.data?.attributes ?? {}

  const { session } = useAuthenticated()
  const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999
  const [loadSimilarVendors, { data: similarVendorsData }] = useSimilarSolutionsLazyQuery()
  const similars = useMemo<ISimilarSolutionItemFragment[]>(() => {
    return (
      similarVendorsData?.similarSolutions.solutions.filter((item): item is ISimilarSolutionItemFragment => !!item) ||
      []
    )
  }, [similarVendorsData])

  const fetchData = async () => {
    const from = router.query.from as string
    const selectedTopic = {
      slug: 'consultants',
      name: 'Consultants',
    }
    setTopicInfo(selectedTopic)

    solution.attributes?.subTopics?.data.map((subTopic) => {
      if (subTopic.attributes?.slug === from) {
        const selected: SimpleSubTopicType = {
          slug: subTopic.attributes.slug || '',
          name: subTopic.attributes.name,
        }
        setSubTopicInfo(selected)
      }
    })

    loadSimilarVendors({
      variables: {
        id: parseInt(solution.id || ''),
        from: from,
      },
    })
  }

  useEffect(() => {
    if (enVariables.ENVIRONMENT === 'production') {
      mixpanel.track('visit_product', {
        model: 'Consultant',
        displayName,
        id: solution.id,
        subtopic: solution.attributes?.subTopics?.data.map((item) => item.attributes?.name),
        isEnhanced,
        isPremium,
      })
    }
  }, [solution, displayName, isEnhanced, isPremium])

  useEffect(() => {
    fetchData()
    if (router.query['from'] === 'submitReview') onSelectTab(REVIEWS_TAB)
  }, [router])

  const meta = (
    <Meta title={displayName} description={shortDescription} url={`/consultants/${slug}/`} image={imageUrl} />
  )
  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <ThemeOnly theme="ilta">
          <IltaHeader />
        </ThemeOnly>
        <div className="flex flex-col bg-clouds">
          <div className="flex flex-col w-full items-center">
            {router.query['viewType'] === 'manage' && (
              <Breadcrumb
                subject={'MY ACCOUNT'}
                subjectRoute={'/my-account'}
                subtopic={'MY SOLUTIONS'}
                subtopicRoute={'/my-solutions'}
                subtitle={displayName}
              />
            )}

            {router.query['viewType'] !== 'manage' && (
              <Fragment>
                {prevPath ? (
                  <SolutionBreadcrumb />
                ) : (
                  <div className="!max-w-[1100px] flex flex-row w-full mt-[20px] mb-[10px] pl-[1.25rem]">
                    <Breadcrumb
                      subject={topicInfo?.name}
                      subjectRoute={`/topics/${topicInfo?.slug}`}
                      subtopic={subTopicInfo?.name}
                      subtopicRoute={`/topics/${topicInfo?.slug}/${subTopicInfo?.slug}`}
                      subtitle={displayName}
                    />
                  </div>
                )}
              </Fragment>
            )}
          </div>

          {(isEnhanced || isPremium) && !!solution.attributes?.consultantData?.enhancedListingData ? (
            <SolutionEnhancedHeader
              key={randomNum + 'SolutionEnhancedHeader'}
              solution={solution}
              onSelectTab={onSelectTab}
              tabIdx={tabIdx}
              showVendorAnalysis={showVendorAnalysis}
              vendorUsers={vendorUsers}
            />
          ) : (
            <SolutionHeader
              key={randomNum + 'SolutionHeader'}
              solution={solution}
              tabIdx={tabIdx}
              onSelectTab={onSelectTab}
              showVendorAnalysis={showVendorAnalysis}
              vendorUsers={vendorUsers}
            />
          )}
        </div>
        <hr />
        {announcement && publishedAt && (
          <div className="flex justify-center">
            <div className="flex flex-col pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full">
              {optionalLink ? (
                <AnnouncementNotification
                  key={randomNum + 'AnnouncementNotification-link'}
                  announcement={announcement}
                  publishedAt={publishedAt}
                  optionalLink={optionalLink}
                />
              ) : (
                <AnnouncementNotification
                  key={randomNum + 'AnnouncementNotification'}
                  announcement={announcement}
                  publishedAt={publishedAt}
                />
              )}
            </div>
          </div>
        )}
        {!isEnhanced && router.query['viewType'] === 'manage' && vendorUsers.includes(session?.user.id || '') && (
          <EnhancedRequestContainer key={randomNum + 'EnhancedRequestContainer'} solution_id={solution.id ?? ''} />
        )}
        <div className="flex flex-row justify-center w-full">
          <div className="!max-w-[1100px] flex flex-row justify-between w-full mb-32">
            {(isEnhanced || isPremium) && !!solution.attributes?.consultantData?.enhancedListingData ? (
              <ConsultantEnhancedDetailContainer
                key={randomNum + 'ConsultantEnhancedDetailContainer'}
                consultant={solution}
                tabIdx={tabIdx}
                similars={similars}
              />
            ) : (
              <ConsultantDetailContainer
                key={randomNum + 'ConsultantDetailContainer'}
                consultant={solution}
                tabIdx={tabIdx}
                similars={similars}
              />
            )}
          </div>
        </div>
        {(isEnhanced || isPremium) && testimonials?.length > 0 && (
          <ThemeOnly theme="legaltech">
            <Testimonials key={randomNum + 'testimonials'} testimonials={testimonials || []} />
          </ThemeOnly>
        )}
        {!isEnhanced && !isPremium && <Testimonials testimonials={_testimonials} />}
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default ConsultantPage

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  if (!params?.slug) return { revalidate: 60, notFound: true }
  try {
    const slug = params.slug as string
    const [{ solutions }, { testimonialsPlural }] = await Promise.all([
      apolloSdk.consultancyBySlug({ slug, includeIlta: enVariables.THEME === 'ilta' }),
      apolloSdk.globalTestimonials(),
    ])

    const solution = solutions && solutions.data.length > 0 ? solutions.data[0] : null
    if (!solution) return { revalidate: 60, notFound: true }

    const vendorUsers =
      (solution.attributes?.vendor?.data?.attributes?.users?.data
        .map((user) => user?.attributes?.external_id || null)
        .filter((item) => item !== null) as string[]) || []

    const testimonials: ITestimonialItemFragment[] = (
      (solution.attributes?.consultantData?.enhancedListingData?.testimonials as any) || []
    ).map((testimonial: any) => {
      return {
        id: testimonial?.id,
        attributes: {
          description: testimonial?.description || '',
          author: {
            name: testimonial?.author?.name || '',
            role: testimonial?.author?.role || '',
            company: testimonial?.author?.company || '',
            avatar: {
              data: {
                attributes: {
                  url: testimonial?.author?.avatar?.data?.attributes?.url || '',
                },
              },
            },
          },
        },
      }
    })
    const { contentResults } = await apolloSdk.searchContents({
      query: { contentType: 'Vendor_Analysis', resourceSolutionIds: solution.id },
    })

    const filters: FilterOption[] = await Promise.all([
      getAuthorsFilter({ includeAuthors: true, includeExperts: true }),
      getDateFilter(),
    ])

    return {
      props: {
        solution,
        testimonials,
        filters,
        showVendorAnalysis: contentResults.data?.total !== 0,
        vendorUsers,
        _testimonials: testimonialsPlural?.data || [],
      },
      revalidate: 60,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error occurred in getStaticProps of Consultants:', error)
    // Graceful fallback in case of an upstream error
    return {
      notFound: true,
      revalidate: 60, // Reattempt fetching during the next static regeneration
    }
  }
}

export async function getStaticPaths() {
  const { solutions } = await apolloSdk.allSolutionSlugs({ start: 0, type: 'Consultancy' })
  const result = (solutions && solutions.data) || []

  const paths = result?.filter((item) => !!item).map((item) => ({ params: { slug: item?.attributes?.slug } }))
  return {
    paths,
    fallback: 'blocking',
  }
}
